import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Save, FileDown, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { DiscoveryFields } from "@/components/DiscoveryFields";

export default function FirmProfile() {
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { user } = useAuth();
  const writable = ["owner", "marketing_director"].includes(user?.role);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quotePdfLoading, setQuotePdfLoading] = useState(false);
  const money = (n) => "$" + (n || 0).toLocaleString();

  useEffect(() => {
    api
      .get("/firm/profile")
      .then((r) => {
        setProfile(r.data.discovery || {});
        setName(r.data.name || "");
      })
      .catch(() => setProfile({}));
    api.get("/firm/scope").then((r) => setScope(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/firm/profile", { discovery: profile });
      toast.success("Firm profile saved");
      api.get("/firm/scope").then((r) => setScope(r.data)).catch(() => {});
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const generateQuote = async () => {
    setQuoteLoading(true);
    try {
      const r = await api.get("/firm/scope/quote");
      setQuote(r.data);
    } catch {
      toast.error("Could not generate quote");
    } finally {
      setQuoteLoading(false);
    }
  };

  const downloadQuotePdf = async () => {
    setQuotePdfLoading(true);
    try {
      const res = await api.post("/firm/scope/quote/pdf", {}, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "retainproof-custom-scope-quote.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    } finally {
      setQuotePdfLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      if (writable) await api.put("/firm/profile", { discovery: profile });
      const res = await api.post("/firm/profile/pdf", {}, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "retainproof-findings.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Findings PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    } finally {
      setPdfLoading(false);
    }
  };

  if (profile === null)
    return <div className="font-mono-data text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="max-w-4xl" data-testid="firm-profile-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Discovery</p>
          <h1 className="font-display text-4xl font-light mt-1">Firm Profile</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            {name ? `${name} — ` : ""}the discovery record captured during consultation.{" "}
            {writable ? "Keep it current as the engagement evolves." : "Read-only for your role."}
          </p>
        </div>
        {writable && (
          <Button onClick={save} disabled={saving} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-firm-profile-button">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save
          </Button>
        )}
        <Button onClick={downloadPdf} disabled={pdfLoading} variant="outline" className="rounded-none border-zinc-300 ml-2" data-testid="download-findings-pdf-button">
          {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Findings PDF
        </Button>
      </div>

      {/* Baseline Marketing Assessment scope tracker */}
      {scope && (
        <div className="border border-zinc-200 bg-white mb-8" data-testid="scope-tracker">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
            <div>
              <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Standard Baseline Marketing Assessment scope</p>
              <h2 className="font-display text-xl mt-0.5">Scope check</h2>
            </div>
            {scope.custom_scope_needed ? (
              <span className="inline-flex items-center gap-1.5 font-mono-data text-[11px] uppercase tracking-widest bg-[#8A6A1F]/10 text-[#8A6A1F] border border-[#8A6A1F]/30 px-3 py-1.5" data-testid="scope-custom-flag">
                <AlertTriangle className="h-3.5 w-3.5" /> Custom-scope quote needed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-mono-data text-[11px] uppercase tracking-widest bg-[#156F52]/10 text-[#156F52] border border-[#156F52]/30 px-3 py-1.5" data-testid="scope-standard-flag">
                <CheckCircle2 className="h-3.5 w-3.5" /> Within standard scope
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-100">
            {scope.dimensions.map((d) => (
              <div key={d.key} className={`p-5 ${d.over ? "bg-[#8A6A1F]/5" : ""}`} data-testid={`scope-dim-${d.key}`}>
                <div className="flex items-center gap-1.5">
                  {d.over ? <AlertTriangle className="h-3.5 w-3.5 text-[#8A6A1F]" /> : <CheckCircle2 className="h-3.5 w-3.5 text-[#156F52]" />}
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">{d.label}</p>
                </div>
                <p className="font-display text-3xl mt-2">
                  {d.actual}
                  <span className="text-base text-zinc-400"> / {d.limit}{d.key === "months" ? " mo" : ""}</span>
                </p>
                <p className={`text-xs mt-1 ${d.over ? "text-[#8A6A1F]" : "text-zinc-400"}`}>
                  {d.over ? `${d.actual - d.limit} over the included limit` : (d.note || "Included in Baseline Marketing Assessment")}
                </p>
              </div>
            ))}
          </div>
          <p className="px-6 py-3 text-xs text-zinc-500 border-t border-zinc-100">
            The Baseline Marketing Assessment (introductory $1,250) includes up to 3 vendors, 6 channels, 3 months of data and 1 location.
            {scope.custom_scope_needed
              ? " This firm exceeds the standard scope — prepare a customized scope quote."
              : " This firm fits the standard scope."}
          </p>
        </div>
      )}

      {scope?.custom_scope_needed && (
        <div className="border border-[#8A6A1F]/40 bg-[#FBF7E9] mb-8" data-testid="scope-quote-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-[#8A6A1F]/20">
            <div>
              <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#8A6A1F]">Custom scope</p>
              <h2 className="font-display text-xl mt-0.5">This firm exceeds the standard scope</h2>
              <p className="text-xs text-zinc-500 mt-1">Generate a one-click quote for the additional vendors, channels and locations.</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button onClick={generateQuote} disabled={quoteLoading} className="bg-[#0B1F33] text-white rounded-none" data-testid="generate-quote-button">
                {quoteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Generate quote
              </Button>
              {quote && (
                <Button onClick={downloadQuotePdf} disabled={quotePdfLoading} variant="outline" className="rounded-none border-zinc-300" data-testid="download-quote-pdf-button">
                  {quotePdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                  Quote PDF
                </Button>
              )}
            </div>
          </div>
          {quote && (
            <div className="px-6 py-4" data-testid="scope-quote-result">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Add&rsquo;l</th>
                    <th className="py-2 text-right">Rate</th>
                    <th className="py-2 text-right">Billing</th>
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#8A6A1F]/10">
                  {quote.line_items.map((li) => (
                    <tr key={li.key} data-testid={`quote-line-${li.key}`}>
                      <td className="py-2 text-zinc-800">{li.label}</td>
                      <td className="py-2 text-right text-zinc-600">{li.extra}</td>
                      <td className="py-2 text-right text-zinc-600">{money(li.rate)}</td>
                      <td className="py-2 text-right text-zinc-600">{li.billing === "month" ? "/mo" : "one-time"}</td>
                      <td className="py-2 text-right font-medium text-zinc-900">{money(li.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex flex-col items-end gap-1">
                {quote.monthly_total > 0 && (
                  <p className="text-sm text-zinc-600" data-testid="quote-monthly-total">
                    Additional monthly: <span className="font-display text-lg text-[#8A6A1F]">{money(quote.monthly_total)}/mo</span>
                  </p>
                )}
                {quote.one_time_total > 0 && (
                  <p className="text-sm text-zinc-600" data-testid="quote-onetime-total">
                    Additional one-time: <span className="font-display text-lg text-[#8A6A1F]">{money(quote.one_time_total)}</span>
                  </p>
                )}
              </div>
              <p className="mt-3 text-[11px] text-zinc-500">Estimate added to your standard plan pricing; final pricing confirmed on your scope of work.</p>
            </div>
          )}
        </div>
      )}

      <div className="border border-zinc-200 bg-white p-8">
        <DiscoveryFields value={profile} onChange={setProfile} readOnly={!writable} />
      </div>

      {writable && (
        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-firm-profile-button-bottom">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save profile
          </Button>
        </div>
      )}
    </div>
  );
}
